import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Modal, Space, Tag, Typography } from 'antd';
import { EyeOutlined, TableOutlined } from '@ant-design/icons';
import { apiClient, apiErrorMessage } from '../utils';
import { Filter, FilterGroup } from '../models';
import { ConditionsViewer } from './conditions-viewer';
import { RecordsPreview } from './records-preview';

interface FilterViewerProps {
  filterGroup?: FilterGroup;
  dataSetId?: string;
  size?: 'normal' | 'small';
}

export function FilterViewer(props: FilterViewerProps) {
  const [filters, setFilters] = useState<Filter[]>([]);
  const [loading, setLoading] = useState(false);
  const [conditionsModalVisible, setConditionsModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedFilterPreview, setSelectedFilterPreview] = useState<Filter | null>(null);

  const filterGroupRef = useRef<FilterGroup>(props.filterGroup ?? { logic: 'AND', filterIds: [] });

  useEffect(() => {
    filterGroupRef.current = props.filterGroup ?? { logic: 'AND', filterIds: [] };
  }, [props.filterGroup]);

  const loadFilters = useCallback(() => {
    if (!props.filterGroup?.filterIds?.length) {
      setFilters([]);
      return;
    }

    setLoading(true);

    const params = [];
    if (props.dataSetId) {
      params.push(`dataSetId=${props.dataSetId}`);
    }
    let url = 'filters';
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    apiClient
      .get(url)
      .then((response: any) => {
        const allFilters = (response.data?.filters as Filter[]) || [];
        const selectedFilters = allFilters.filter(f => props.filterGroup!.filterIds.includes(f.id));
        selectedFilters.sort((a, b) => {
          const indexA = props.filterGroup!.filterIds.indexOf(a.id);
          const indexB = props.filterGroup!.filterIds.indexOf(b.id);
          return indexA - indexB;
        });
        setFilters(selectedFilters);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props.filterGroup, props.dataSetId]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const handleConditionsClick = () => {
    setConditionsModalVisible(true);
  };

  const handleConditionsClose = () => {
    setConditionsModalVisible(false);
  };

  const handlePreviewClick = () => {
    setSelectedFilterPreview(null);
    setPreviewModalVisible(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalVisible(false);
    setSelectedFilterPreview(null);
  };

  const handleFilterPreviewClick = (filter: Filter) => {
    setSelectedFilterPreview(filter);
    setPreviewModalVisible(true);
  };

  const LogicTag = ({ logic }: { logic: 'AND' | 'OR' }) => (
    <Tag
      style={{
        borderRadius: '12px',
        backgroundColor: logic === 'AND' ? '#e6f7ff' : '#fff7e6',
        border: `1px solid ${logic === 'AND' ? '#91d5ff' : '#ffd591'}`,
        color: logic === 'AND' ? '#1890ff' : '#fa8c16',
        fontWeight: 600,
        margin: '0 8px',
      }}
    >
      {logic === 'AND' ? 'VE' : 'VEYA'}
    </Tag>
  );

  const selectSize = props.size === 'small' ? 'small' : 'middle';
  const logic = props.filterGroup?.logic ?? 'AND';

  if (!props.filterGroup?.filterIds?.length && !loading) {
    return (
      <div
        style={{
          height: selectSize === 'small' ? 24 : 32,
          display: 'flex',
          alignItems: 'center',
          padding: '0 11px',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          backgroundColor: '#fafafa',
        }}
      >
        <Typography.Text type="secondary" style={{ fontSize: selectSize === 'small' ? 12 : 14 }}>
          Filtre seçilmemiş
        </Typography.Text>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', gap: selectSize === 'small' ? '6px' : '8px', width: '100%' }}>
        <Tag
          style={{
            display: 'flex',
            alignItems: 'center',
            height: selectSize === 'small' ? 24 : 32,
            width: selectSize === 'small' ? 75 : 80,
            justifyContent: 'center',
            borderRadius: '6px',
            backgroundColor: logic === 'AND' ? '#e6f7ff' : '#fff7e6',
            border: `1px solid ${logic === 'AND' ? '#91d5ff' : '#ffd591'}`,
            color: logic === 'AND' ? '#1890ff' : '#fa8c16',
            fontWeight: 600,
            margin: 0,
            flexShrink: 0,
          }}
        >
          {logic === 'AND' ? 'VE' : 'VEYA'}
        </Tag>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            height: selectSize === 'small' ? 24 : 32,
            display: 'flex',
            alignItems: 'center',
            padding: '0 11px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            backgroundColor: '#fafafa',
            cursor: 'default',
          }}
        >
          {loading ? (
            <Typography.Text type="secondary" style={{ fontSize: selectSize === 'small' ? 12 : 14 }}>
              Yükleniyor...
            </Typography.Text>
          ) : filters.length > 0 ? (
            <Typography.Text ellipsis style={{ fontSize: selectSize === 'small' ? 12 : 14 }}>
              {filters.map(f => f.name).join(', ')}
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: selectSize === 'small' ? 12 : 14 }}>
              Filtre seçilmemiş
            </Typography.Text>
          )}
        </div>
        <Button
          icon={<EyeOutlined />}
          onClick={handleConditionsClick}
          disabled={!filters.length}
          size={selectSize}
          style={{ flexShrink: 0 }}
          title="Koşulları görüntüle"
        />
        <Button
          icon={<TableOutlined />}
          onClick={handlePreviewClick}
          disabled={!filters.length}
          size={selectSize}
          style={{ flexShrink: 0 }}
          title="Önizleme"
        />
      </div>

      <Modal
        title="Filtre koşulları"
        visible={conditionsModalVisible}
        onCancel={handleConditionsClose}
        footer={
          <Button type="primary" icon={<TableOutlined />} onClick={() => handlePreviewClick()}>
            Veri önizleme (tümü)
          </Button>
        }
        width="80%"
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {filters.map((filter, index) => (
            <React.Fragment key={filter.id}>
              {index > 0 && (
                <div style={{ textAlign: 'center', margin: '16px 0' }}>
                  <LogicTag logic={logic} />
                </div>
              )}
              <Card
                size="small"
                title={
                  <Space>
                    <Typography.Text strong>{filter.name}</Typography.Text>
                    {filter.description && (
                      <Typography.Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                        ({filter.description})
                      </Typography.Text>
                    )}
                  </Space>
                }
                extra={
                  <Button
                    size="small"
                    icon={<TableOutlined />}
                    onClick={() => handleFilterPreviewClick(filter)}
                  >
                    Veri önizleme
                  </Button>
                }
              >
                <ConditionsViewer conditions={filter.conditionsDisplay} layout="vertical" />
              </Card>
            </React.Fragment>
          ))}
        </Space>
      </Modal>

      <Modal
        title={selectedFilterPreview ? `Veri önizleme: ${selectedFilterPreview.name}` : 'Veri önizleme: Tümü'}
        visible={previewModalVisible}
        onCancel={handlePreviewClose}
        footer={null}
        width="80%"
        destroyOnClose
      >
        {selectedFilterPreview ? (
          <RecordsPreview dataSetId={props.dataSetId} filter={selectedFilterPreview} />
        ) : (
          <RecordsPreview dataSetId={props.dataSetId} filterGroup={filterGroupRef.current} />
        )}
      </Modal>
    </>
  );
}
